import { useMutation } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import {
  Dialog,
  Text,
  Actions,
  Button,
  FieldMessage,
  Stack,
  IconDelete,
} from 'braid-design-system';
import { useState } from 'react';

import {
  ActionOrigin,
  Page,
  PageSection,
  UpdateType,
  useTrackingClient,
} from 'src/context/tracking';
import { useProfileAnalytic } from 'src/hooks/useProfileAnalytic';
import { logError } from 'src/modules/error-logger/browserLog';
import { getOperationNameFromQuery } from 'src/modules/utils';
import { useDocuments } from 'src/pages/Documents/hooks/useDocuments';

import translations from './.vocab';
import { DELETE_RESUME } from './delete-resume.graphql';

interface Props {
  resumeId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const DocumentDeleteDialog = ({
  resumeId,
  open,
  onClose,
  onSuccess,
}: Props) => {
  const [isErrorMessageVisible, setErrorMessageVisibility] = useState(false);
  const {
    profileApproachabilityStatus,
    profileSharedStatus,
    profileVisibilityLevel,
  } = useProfileAnalytic();

  // TODO: Delete other document types
  const [deleteResume] = useMutation(DELETE_RESUME, {
    variables: {
      input: { id: resumeId },
    },
  });

  // TODO: Tracking for all document types
  const client = useTrackingClient();
  // TODO: Get different document types (pass as prop?)
  const { resumes } = useDocuments();
  const resume = resumes.find((r) => r.id === resumeId);

  const handleClose = () => {
    setErrorMessageVisibility(false);
    onClose();
  };

  const { t } = useTranslations(translations);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      id="delete-resume-dialog"
      data={{ testid: 'delete-resume-dialog' }}
      title={t('Are you sure you want to delete')}
      closeLabel={t('Dismiss')}
    >
      <Text>{resume?.fileMetadata?.name}</Text>
      <Stack space="small">
        <Actions>
          <Button
            data={{ testid: 'delete-confirmation' }}
            tone="critical"
            icon={<IconDelete />}
            onClick={() => {
              if (!Boolean(resumeId)) return;
              deleteResume()
                .then(() => {
                  client.trackLink({
                    link: 'profile_update_succeeded',
                    currentPage: Page.DOCUMENT_PAGE,
                    actionOrigin: ActionOrigin.APPLY,
                    updateType: UpdateType.DELETE,
                    currentPageSection: PageSection.RESUME,
                    profileApproachabilityStatus,
                    profileSharedStatus,
                    profileVisibilityLevel,
                    resumeHasSensitivePersonalInfo: Boolean(
                      resume?.fileMetadata?.sensitiveDataInfo?.isDetected,
                    ),
                  });
                  onSuccess();
                })
                .catch((err) => {
                  setErrorMessageVisibility(true);
                  logError(
                    {
                      ...err,
                      type: 'delete-resume',
                    },
                    {
                      error: err,
                      stack: err.stack,
                      resumeId,
                      operationName: getOperationNameFromQuery(DELETE_RESUME),
                    },
                  );
                });
            }}
          >
            {t('Delete')}
          </Button>
          <Button variant="transparent" tone="formAccent" onClick={handleClose}>
            {t('Cancel')}
          </Button>
        </Actions>

        {Boolean(isErrorMessageVisible) && (
          <FieldMessage
            id="0"
            message={t('Could not delete')}
            tone="critical"
          />
        )}
      </Stack>
    </Dialog>
  );
};
